import type { ReactNode } from 'react';
import { useTranslation } from '@/lib/i18n';
import { cn } from '@/lib/utils';

export function Skeleton({ className }: { className?: string }) {
  return <span className={cn('block animate-pulse rounded-md bg-muted', className)} aria-hidden />;
}

const TEXT_WIDTHS = ['w-full', 'w-11/12', 'w-4/5', 'w-2/3', 'w-5/6', 'w-3/4'];

export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  const common = useTranslation('common');
  return (
    <div className={cn('space-y-2', className)} role="status" aria-label={common.t('state.loading')}>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton key={index} className={cn('h-3.5', TEXT_WIDTHS[index % TEXT_WIDTHS.length])} />
      ))}
    </div>
  );
}

export function TableSkeleton({
  columns = 4,
  rows = 6,
  dense,
  className,
}: {
  columns?: number;
  rows?: number;
  dense?: boolean;
  className?: string;
}) {
  const common = useTranslation('common');
  const cells = Array.from({ length: Math.max(1, columns) }, (_, index) => index);
  return (
    <div className={cn('overflow-hidden rounded-xl border bg-card shadow-sm', className)} role="status" aria-label={common.t('state.loading')}>
      <div className="flex gap-4 border-b bg-muted/60 px-3 py-3">
        {cells.map((cell) => (
          <Skeleton key={cell} className={cn('h-3', cell === 0 ? 'w-32' : 'w-20', cell === cells.length - 1 && 'ms-auto w-14')} />
        ))}
      </div>
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className={cn('flex items-center gap-4 border-b border-border/70 px-3 last:border-0 odd:bg-muted/20', dense ? 'py-2.5' : 'py-3.5')}>
          {cells.map((cell) => (
            <Skeleton
              key={cell}
              className={cn('h-3.5', cell === 0 ? 'w-40' : cell % 3 === 0 ? 'w-16' : 'w-24', cell === cells.length - 1 && 'ms-auto w-12')}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function FieldSkeleton({ wide }: { wide?: boolean }) {
  return (
    <div className={cn('space-y-2', wide && 'sm:col-span-2')}>
      <Skeleton className="h-3 w-24" />
      <Skeleton className="h-9 w-full" />
    </div>
  );
}

export function FormSkeleton({ sections = 2, className }: { sections?: number; className?: string }) {
  const common = useTranslation('common');
  return (
    <div className={cn('space-y-5', className)} role="status" aria-label={common.t('state.loading')}>
      {Array.from({ length: sections }, (_, section) => (
        <div key={section} className="rounded-xl border bg-card shadow-sm">
          <div className="flex items-center gap-3 border-b px-5 py-3.5">
            <Skeleton className="h-8 w-8 rounded-lg" />
            <Skeleton className="h-3.5 w-36" />
          </div>
          <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
            <FieldSkeleton />
            <FieldSkeleton />
            <FieldSkeleton wide={section === 0} />
            {section !== 0 && <FieldSkeleton />}
          </div>
        </div>
      ))}
    </div>
  );
}

export function PageSkeleton({ className }: { className?: string }) {
  const common = useTranslation('common');
  return (
    <div className={cn('space-y-5', className)} role="status" aria-label={common.t('state.loading')}>
      <div className="flex items-start gap-3">
        <Skeleton className="h-11 w-11 rounded-xl" />
        <div className="flex-1 space-y-2 pt-1">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-3.5 w-80 max-w-full" />
        </div>
        <Skeleton className="h-9 w-28" />
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        {[0, 1, 2].map((tile) => (
          <div key={tile} className="space-y-2 rounded-xl border bg-card p-4 shadow-sm">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-6 w-28" />
          </div>
        ))}
      </div>
      <TableSkeleton rows={4} />
    </div>
  );
}

export function ProgressBar({ active = true, className }: { active?: boolean; className?: string }) {
  const common = useTranslation('common');
  if (!active) return null;
  return (
    <div className={cn('relative h-0.5 w-full overflow-hidden bg-primary/15', className)} role="progressbar" aria-busy="true" aria-label={common.t('state.loading')}>
      <span className="loader-slide absolute inset-y-0 w-1/3 rounded-full bg-primary" />
    </div>
  );
}

export function BrandLoader({
  label,
  fullScreen,
  className,
  children,
}: {
  label?: ReactNode;
  fullScreen?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn('flex flex-col items-center justify-center gap-5 text-center', fullScreen && 'min-h-screen bg-background', className)}
    >
      <div className="relative h-20 w-20">
        <span className="absolute inset-0 rounded-full border-2 border-primary/15" />
        <span className="loader-orbit absolute inset-0 rounded-full border-2 border-transparent border-t-primary" />
        <span className="loader-orbit-reverse absolute inset-[5px] rounded-full border border-transparent border-b-primary/40" />
        <span className="loader-breathe absolute inset-4 grid place-items-center rounded-2xl bg-primary text-sm font-bold tracking-wide text-primary-foreground shadow-lg shadow-primary/30">
          HIS
        </span>
      </div>
      <svg width="140" height="28" viewBox="0 0 140 28" className="text-primary" aria-hidden>
        <path
          d="M0 14 H36 L44 14 L50 4 L58 25 L65 9 L71 14 H140"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="loader-trace"
        />
      </svg>
      {label && <p className="text-sm text-muted-foreground">{label}</p>}
      {children}
    </div>
  );
}
